import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, Modal, Form, InputNumber, Select, DatePicker, message, Space, Input, Tag, Alert, Popconfirm, Tooltip, Card, Row, Col, Statistic, Grid, Divider, Checkbox, Collapse } from 'antd';
import { PlusOutlined, SearchOutlined, CheckOutlined, DeleteOutlined, ExperimentOutlined, WarningOutlined, FilePdfOutlined, EditOutlined, ScheduleOutlined, UnorderedListOutlined, CheckCircleFilled, AppstoreOutlined } from '@ant-design/icons';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { uretimlerAPI, urunlerAPI, gununMenuleriAPI, musterilerAPI, gunUretimPlaniAPI, takvimEkstraMenuleriAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import { formatPara, formatSayi } from '../utils/format';
import UrunDetayModal from '../components/UrunDetayModal';
import ReceteModal from '../components/ReceteModal';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

dayjs.locale('tr');

const gunKeyMap = {
  1: 'pazartesi_kisi',
  2: 'sali_kisi',
  3: 'carsamba_kisi',
  4: 'persembe_kisi',
  5: 'cuma_kisi',
  6: 'cumartesi_kisi',
  0: 'pazar_kisi',
};

const gunAdlari = { 1: 'Pazartesi', 2: 'Salı', 3: 'Çarşamba', 4: 'Perşembe', 5: 'Cuma', 6: 'Cumartesi', 0: 'Pazar' };

function Uretimler() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [data, setData] = useState([]);
  const [urunler, setUrunler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aramaMetni, setAramaMetni] = useState('');

  const [hazirlaOpen, setHazirlaOpen] = useState(false);
  const [hazirlaForm] = Form.useForm();
  const [malzemeIhtiyac, setMalzemeIhtiyac] = useState([]);
  const [ihtiyacLoading, setIhtiyacLoading] = useState(false);
  const [hazirlaAdim, setHazirlaAdim] = useState(1);

  // Üretim Planı modal — sabit müşteri override + sıcak satış + ekstra menü/ürün
  const [planEditMusteriler, setPlanEditMusteriler] = useState([]); // [{cari_id, ad, kisi, dahil}]
  const [planEditSicak, setPlanEditSicak] = useState(0); // günün menüsünden sıcak satış için kişi
  const [planEditEkstralar, setPlanEditEkstralar] = useState([]); // [{kaynak_tip, kaynak_id, ad, adet}]
  const [gununEkstraMenuleri, setGununEkstraMenuleri] = useState([]); // o güne tanımlı alternatif menüler
  const [ekstraEklePopOpen, setEkstraEklePopOpen] = useState(false);
  const [ekstraEkleSecim, setEkstraEkleSecim] = useState(null); // 'menu:5' | 'urun:12'
  const [ekstraEkleAdet, setEkstraEkleAdet] = useState(1);
  const [planKayitli, setPlanKayitli] = useState(false);
  const [planSaveLoading, setPlanSaveLoading] = useState(false);

  const [duzenleOpen, setDuzenleOpen] = useState(false);
  const [duzenleForm] = Form.useForm();
  const [duzenleRecord, setDuzenleRecord] = useState(null);

  // Günün Planı
  const [planTarih, setPlanTarih] = useState(dayjs());
  const [planMenu, setPlanMenu] = useState(null);
  const [planMusteriler, setPlanMusteriler] = useState([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [detayOpen, setDetayOpen] = useState(false);
  const [urunDetayOpen, setUrunDetayOpen] = useState(false);
  const [urunDetayKayit, setUrunDetayKayit] = useState(null);

  // Malzeme kontrol modali (plan modalından açılır)
  const [malzemeKontrolOpen, setMalzemeKontrolOpen] = useState(false);
  const [malzemeKontrolLoading, setMalzemeKontrolLoading] = useState(false);
  const [malzemeKontrolData, setMalzemeKontrolData] = useState([]);
  const [malzemeKontrolTikler, setMalzemeKontrolTikler] = useState({});
  // Aşama tamamlanma — tarih bazlı, localStorage'da kalıcı
  const MK_TAMAM_LS_KEY = 'uretim_malzeme_kontrol_tamam_map';
  const [malzemeKontrolTamamMap, setMalzemeKontrolTamamMap] = useState(() => {
    try {
      const raw = localStorage.getItem(MK_TAMAM_LS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try { localStorage.setItem(MK_TAMAM_LS_KEY, JSON.stringify(malzemeKontrolTamamMap)); } catch {}
  }, [malzemeKontrolTamamMap]);

  // Reçete modal (Malzeme Kontrol içinden açılır)
  const [receteOpen, setReceteOpen] = useState(false);
  const [receteUrun, setReceteUrun] = useState(null);

  const { birimOptions: birimler } = useLookups();

  const fetchData = async () => {
    setLoading(true);
    const res = await uretimlerAPI.getAll();
    setData(res.data);
    setLoading(false);
  };

  const fetchUrunler = async () => {
    const res = await urunlerAPI.getAll();
    setUrunler(res.data.map(u => ({ value: u.id, label: u.urun_adi, birim_id: u.birim_id })));
  };

  useEffect(() => {
    fetchData();
    fetchUrunler();
  }, []);

  const fetchPlan = async (tarih) => {
    setPlanLoading(true);
    try {
      const tarihStr = tarih.format('YYYY-MM-DD');
      const [menuRes, planRes, ekstraMenuRes] = await Promise.all([
        gununMenuleriAPI.getByDate(tarihStr),
        gunUretimPlaniAPI.getByDate(tarihStr),
        takvimEkstraMenuleriAPI.getByDate(tarihStr),
      ]);
      setPlanMenu(menuRes.data || null);
      setGununEkstraMenuleri(ekstraMenuRes.data || []);
      const plan = planRes.data || { musteriler: [], sicak_satis_kisi: 0, sicak_ekstralar: [], kayitli: false };
      const tumMusteriler = (plan.musteriler || []).map(m => ({
        cari_id: m.cari_id, ad: m.ad, kisi: Number(m.kisi || 0), dahil: m.dahil !== false,
      })).sort((a, b) => (a.ad || '').localeCompare(b.ad || '', 'tr'));
      // Üst kart için sadece dahil müşteriler
      const aktifler = tumMusteriler
        .filter(m => m.dahil && m.kisi > 0)
        .map(m => ({ id: m.cari_id, ad: m.ad, kisi: m.kisi }));
      setPlanMusteriler(aktifler);
      setPlanEditMusteriler(tumMusteriler);
      setPlanEditSicak(Number(plan.sicak_satis_kisi || 0));
      setPlanEditEkstralar((plan.sicak_ekstralar || []).map(e => ({
        kaynak_tip: e.kaynak_tip, kaynak_id: e.kaynak_id, ad: e.ad, adet: Number(e.adet || 0),
      })));
      setPlanKayitli(!!plan.kayitli);
    } catch (err) {
      message.error('Günün planı alınamadı');
    } finally {
      setPlanLoading(false);
    }
  };

  useEffect(() => { fetchPlan(dayjs()); }, []);

  const planSabitToplam = planMusteriler.reduce((s, m) => s + m.kisi, 0);
  const planToplamKisi = planSabitToplam + Number(planEditSicak || 0);

  const planEditSabitToplam = planEditMusteriler
    .filter(m => m.dahil)
    .reduce((s, m) => s + Number(m.kisi || 0), 0);
  const planEditGunMenuKisi = planEditSabitToplam + Number(planEditSicak || 0);

  const handlePlanKaydet = async () => {
    setPlanSaveLoading(true);
    try {
      await gunUretimPlaniAPI.upsert({
        tarih: planTarih.format('YYYY-MM-DD'),
        sicak_satis_kisi: Number(planEditSicak || 0),
        musteriler: planEditMusteriler.map(m => ({
          cari_id: m.cari_id, kisi: Number(m.kisi || 0), dahil: !!m.dahil,
        })),
        sicak_ekstralar: planEditEkstralar.map(e => ({
          kaynak_tip: e.kaynak_tip, kaynak_id: e.kaynak_id, adet: Number(e.adet || 0),
        })),
      });
      message.success('Üretim planı kaydedildi');
      setDetayOpen(false);
      fetchPlan(planTarih); // listede toplamlar tazelensin
    } catch (err) {
      message.error('Plan kaydedilemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setPlanSaveLoading(false);
    }
  };

  // Günün menüsü kalemleri × toplam kişi (sabit dahil + sıcak satışın günün menüsü payı)
  const planUretimler = (planMenu && planToplamKisi > 0)
    ? (planMenu.kalemler || []).map(k => ({
        urun_id: k.urun_id,
        urun_adi: k.urun_adi,
        adet_per_kisi: Number(k.adet || 0),
        toplam: Number(k.adet || 0) * planToplamKisi,
      }))
    : [];

  // Plan modali edit-state'inden agrege ürün listesi (üretilecekler)
  const planEditUretilecekler = useMemo(() => {
    const acc = {}; // urun_id -> { urun_id, urun_adi, miktar }
    if (planMenu && planEditGunMenuKisi > 0) {
      (planMenu.kalemler || []).forEach(k => {
        if (!acc[k.urun_id]) acc[k.urun_id] = { urun_id: k.urun_id, urun_adi: k.urun_adi, miktar: 0 };
        acc[k.urun_id].miktar += Number(k.adet || 0) * planEditGunMenuKisi;
      });
    }
    planEditEkstralar.forEach(e => {
      if (e.kaynak_tip === 'menu') {
        const menu = gununEkstraMenuleri.find(m => m.id === e.kaynak_id);
        (menu?.kalemler || []).forEach(k => {
          if (!acc[k.urun_id]) acc[k.urun_id] = { urun_id: k.urun_id, urun_adi: k.urun_adi, miktar: 0 };
          acc[k.urun_id].miktar += Number(k.adet || 0) * Number(e.adet || 0);
        });
      } else {
        const u = urunler.find(x => x.value === e.kaynak_id);
        const adi = u?.label || e.ad || '?';
        if (!acc[e.kaynak_id]) acc[e.kaynak_id] = { urun_id: e.kaynak_id, urun_adi: adi, miktar: 0 };
        acc[e.kaynak_id].miktar += Number(e.adet || 0);
      }
    });
    return Object.values(acc);
  }, [planMenu, planEditGunMenuKisi, planEditEkstralar, gununEkstraMenuleri, urunler]);

  const acMalzemeKontrol = async () => {
    if (planEditUretilecekler.length === 0) {
      message.warning('Üretilecek ürün yok');
      return;
    }
    setMalzemeKontrolLoading(true);
    try {
      const res = await uretimlerAPI.malzemeIhtiyacDetayli(
        planEditUretilecekler.map(u => ({ urun_id: u.urun_id, miktar: u.miktar }))
      );
      setMalzemeKontrolData(res.data);
      // Bu tarih için daha önce "Üretime Devam" basılmışsa tüm tikleri tikli yükle —
      // aksi takdirde her açışta sıfırdan tiklemeyi zorlardı.
      const ts = planTarih.format('YYYY-MM-DD');
      if (malzemeKontrolTamamMap[ts]) {
        const tumTikler = {};
        (res.data || []).forEach(u => (u.malzemeler || []).forEach(m => { tumTikler[m.malzeme_id] = true; }));
        setMalzemeKontrolTikler(tumTikler);
      } else {
        setMalzemeKontrolTikler({});
      }
      setMalzemeKontrolOpen(true);
    } catch (err) {
      message.error('Malzeme kontrolü alınamadı: ' + (err.response?.data?.hata || err.message));
    } finally {
      setMalzemeKontrolLoading(false);
    }
  };

  // Reçete kaydından sonra malzeme kontrol verisini tazele
  const refreshMalzemeKontrol = async () => {
    if (planEditUretilecekler.length === 0) return;
    try {
      const res = await uretimlerAPI.malzemeIhtiyacDetayli(
        planEditUretilecekler.map(u => ({ urun_id: u.urun_id, miktar: u.miktar }))
      );
      setMalzemeKontrolData(res.data);
    } catch {}
  };

  const malzemeKontrolTumIds = useMemo(() => {
    const s = new Set();
    malzemeKontrolData.forEach(u => (u.malzemeler || []).forEach(m => s.add(m.malzeme_id)));
    return Array.from(s);
  }, [malzemeKontrolData]);
  const malzemeKontrolTumTikli = malzemeKontrolTumIds.length > 0
    && malzemeKontrolTumIds.every(id => malzemeKontrolTikler[id]);

  // Tüm ürünler arası birleşik malzeme listesi (aynı malzeme bir tek satır, miktarlar toplanır)
  const birlesikMalzemeler = useMemo(() => {
    const map = {};
    malzemeKontrolData.forEach(u => {
      (u.malzemeler || []).forEach(m => {
        if (!map[m.malzeme_id]) {
          map[m.malzeme_id] = {
            malzeme_id: m.malzeme_id,
            malzeme_adi: m.malzeme_adi,
            birim: m.birim || '',
            toplam_gereken: 0,
            urun_sayisi: 0,
          };
        }
        map[m.malzeme_id].toplam_gereken += Number(m.gereken_miktar) || 0;
        map[m.malzeme_id].urun_sayisi += 1;
      });
    });
    return Object.values(map).sort((a, b) => (a.malzeme_adi || '').localeCompare(b.malzeme_adi || '', 'tr'));
  }, [malzemeKontrolData]);

  // Sıcak satış ekstraları → kart üst özetine grup başlığıyla satır(lar)
  const planEkstraGruplari = planEditEkstralar.map(e => {
    if (e.kaynak_tip === 'menu') {
      const menu = gununEkstraMenuleri.find(m => m.id === e.kaynak_id);
      const ad = menu?.ad || e.ad || 'Menü';
      const kalemler = (menu?.kalemler || []).map(k => ({
        urun_id: k.urun_id, urun_adi: k.urun_adi, toplam: Number(k.adet || 0) * Number(e.adet || 0),
      }));
      return { tip: 'menu', baslik: `${ad} (${e.adet} kişilik sıcak satış)`, kalemler };
    }
    const u = urunler.find(x => x.value === e.kaynak_id);
    const adi = u?.label || e.ad || 'Ürün';
    return { tip: 'urun', baslik: 'Sıcak satış ürünü', kalemler: [{ urun_id: e.kaynak_id, urun_adi: adi, toplam: Number(e.adet || 0) }] };
  });

  const urunSecenekleri = urunler;

  const handleHazirlaAc = () => {
    hazirlaForm.resetFields();
    setHazirlaAdim(1);
    setMalzemeIhtiyac([]);
    // Günün menüsü ve toplam kişi varsa kalemleri otomatik doldur
    if (planMenu && planUretimler.length > 0 && planToplamKisi > 0) {
      const kalemler = (planMenu.kalemler || []).map(k => {
        const u = urunler.find(x => x.value === k.urun_id);
        return { urun_id: k.urun_id, miktar: Number(k.adet || 0) * planToplamKisi, birim_id: u?.birim_id || 6 };
      });
      hazirlaForm.setFieldsValue({ tarih: planTarih, kalemler });
      setHazirlaOpen(true);
      message.success(`${kalemler.length} kalem ${planToplamKisi} kişilik olarak yüklendi`);
      return;
    }
    // Menü yoksa boş asıl modal'ı aç
    hazirlaForm.setFieldsValue({ tarih: planTarih });
    setHazirlaOpen(true);
  };

  // Günün menüsünden kalemleri forma yükle (seçilen tarihin toplam kişi sayısı ile)
  const gununMenusunuYukle = async () => {
    const tarih = hazirlaForm.getFieldValue('tarih') || dayjs();
    const tarihStr = dayjs(tarih).format('YYYY-MM-DD');
    try {
      const [menuRes, musteriRes] = await Promise.all([
        gununMenuleriAPI.getByDate(tarihStr),
        musterilerAPI.getAll(),
      ]);
      const menu = menuRes.data;
      if (!menu || !menu.kalemler?.length) {
        hazirlaForm.setFieldsValue({ kalemler: [] });
        message.warning('Bu tarihe ait günün menüsü tanımlanmamış');
        return;
      }
      const gunKey = gunKeyMap[dayjs(tarih).day()];
      const toplamKisi = (musteriRes.data || [])
        .filter(m => m.aktif && m.cari_aktif)
        .reduce((s, m) => s + Number(m[gunKey] || 0), 0);
      if (toplamKisi <= 0) {
        hazirlaForm.setFieldsValue({ kalemler: [] });
        message.warning(`${gunAdlari[dayjs(tarih).day()]} günü için aktif müşteri kişi sayısı 0`);
        return;
      }
      const kalemler = menu.kalemler.map(k => {
        const u = urunler.find(x => x.value === k.urun_id);
        return {
          urun_id: k.urun_id,
          miktar: Number(k.adet) * toplamKisi,
          birim_id: u?.birim_id || 6,
        };
      });
      hazirlaForm.setFieldsValue({ kalemler });
      message.success(`${kalemler.length} kalem ${toplamKisi} kişilik olarak yüklendi`);
    } catch (err) {
      message.error('Günün menüsü alınamadı');
    }
  };

  const hesaplaIhtiyac = async () => {
    try {
      const values = await hazirlaForm.validateFields();
      const gecerli = (values.kalemler || []).filter(k => k && k.urun_id && k.miktar > 0);
      if (gecerli.length === 0) { message.warning('Geçerli ürün ve miktar girin'); return; }
      setIhtiyacLoading(true);
      const res = await uretimlerAPI.malzemeIhtiyac(gecerli);
      setMalzemeIhtiyac(res.data);
      setHazirlaAdim(2);
    } catch (err) {
      if (err.errorFields) return;
      message.error('Malzeme ihtiyacı hesaplanamadı');
    } finally {
      setIhtiyacLoading(false);
    }
  };

  const handleHazirlaKaydet = async () => {
    try {
      const values = hazirlaForm.getFieldsValue();
      const gecerli = (values.kalemler || []).filter(k => k && k.urun_id && k.miktar > 0 && k.birim_id);
      if (gecerli.length === 0) { message.warning('Geçerli ürün bulunamadı (ürün, miktar ve birim zorunlu)'); return; }
      const payload = { kalemler: gecerli, tarih: dayjs(values.tarih).format('YYYY-MM-DD') };
      const res = await uretimlerAPI.create(payload);
      message.success(res.data.mesaj);
      hazirlaKapat();
      fetchData();
    } catch (err) {
      if (err.errorFields) return;
      message.error('Kayıt sırasında hata: ' + (err.response?.data?.hata || err.message));
    }
  };

  const hazirlaKapat = () => {
    setHazirlaOpen(false);
    setHazirlaAdim(1);
    setMalzemeIhtiyac([]);
    hazirlaForm.resetFields();
  };

  const handleOnayla = async (id) => {
    try {
      const res = await uretimlerAPI.onayla(id);
      message.success(res.data.mesaj);
      if (res.data.uyari) message.warning(res.data.uyari, 6);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.hata || 'Onay sırasında hata');
    }
  };

  const handleSil = async (id) => {
    try {
      const res = await uretimlerAPI.remove(id);
      message.success(res.data.mesaj);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.hata || 'Silme sırasında hata');
    }
  };

  const handleDuzenleAc = (record) => {
    setDuzenleRecord(record);
    duzenleForm.setFieldsValue({
      urun_id: record.urun_id,
      miktar: Number(record.miktar),
      birim_id: record.birim_id,
      tarih: dayjs(record.tarih),
    });
    setDuzenleOpen(true);
  };

  const handleDuzenleKaydet = async () => {
    try {
      const values = await duzenleForm.validateFields();
      const payload = {
        urun_id: values.urun_id,
        miktar: values.miktar,
        birim_id: values.birim_id,
        tarih: dayjs(values.tarih).format('YYYY-MM-DD'),
      };
      const res = await uretimlerAPI.update(duzenleRecord.id, payload);
      message.success(res.data.mesaj);
      setDuzenleOpen(false);
      duzenleForm.resetFields();
      setDuzenleRecord(null);
      fetchData();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.hata || 'Güncelleme sırasında hata');
    }
  };

  const planTarihStr = planTarih ? planTarih.format('YYYY-MM-DD') : null;
  const filteredData = data.filter(u => {
    if (planTarihStr && (u.tarih ? dayjs(u.tarih).format('YYYY-MM-DD') : null) !== planTarihStr) return false;
    if (aramaMetni && !(u.urun_adi || '').toLowerCase().includes(aramaMetni.toLowerCase())) return false;
    return true;
  });

  const strSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '', 'tr');
  const numSorter = (key) => (a, b) => Number(a[key] || 0) - Number(b[key] || 0);

  const columns = [
    { title: 'Tarih', dataIndex: 'tarih', key: 'tarih', width: isMobile ? 65 : 110,
      sorter: (a, b) => { const d = new Date(a.tarih || 0) - new Date(b.tarih || 0); return d !== 0 ? d : (a.id || 0) - (b.id || 0); },
      defaultSortOrder: 'descend',
      render: (val) => val ? dayjs(val).format(isMobile ? 'DD.MM.YY' : 'DD.MM.YYYY') : '-' },
    { title: 'Ürün', dataIndex: 'urun_adi', key: 'urun_adi', sorter: strSorter('urun_adi'),
      render: (text, record) => (
        <a onClick={() => { setUrunDetayKayit({ id: record.urun_id, urun_adi: record.urun_adi }); setUrunDetayOpen(true); }}>{text}</a>
      ),
    },
    { title: 'Miktar', dataIndex: 'miktar', key: 'miktar', sorter: numSorter('miktar') },
    { title: 'Birim', dataIndex: 'birim', key: 'birim', sorter: strSorter('birim'), responsive: ['md'] },
    { title: 'Durum', dataIndex: 'durum', key: 'durum',
      filters: [{ text: 'Beklemede', value: 'beklemede' }, { text: 'Onaylandı', value: 'onaylandi' }],
      onFilter: (value, record) => record.durum === value,
      render: (durum) => <Tag color={durum === 'onaylandi' ? 'green' : 'orange'}>{durum === 'onaylandi' ? 'Onaylandı' : 'Beklemede'}</Tag>
    },
    {
      title: 'İşlem', key: 'islem', width: 160,
      render: (_, record) => (
        <Space>
          {record.durum === 'beklemede' && (
            <Tooltip title="Onayla">
              <Popconfirm title="Bu üretimi onaylamak istediğinize emin misiniz?" description="Onaylandığında stok hareketleri işlenecektir." onConfirm={() => handleOnayla(record.id)} okText="Onayla" cancelText="İptal">
                <Button type="primary" size="small" icon={<CheckOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
          <Tooltip title="Düzenle">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleDuzenleAc(record)} />
          </Tooltip>
          <Tooltip title="Sil">
            <Popconfirm
              title={record.durum === 'onaylandi' ? 'Onaylanmış üretimi silmek istediğinize emin misiniz?' : 'Bu üretimi silmek istediğinize emin misiniz?'}
              description={record.durum === 'onaylandi' ? 'Stok hareketleri geri alınacaktır.' : undefined}
              onConfirm={() => handleSil(record.id)} okText="Sil" cancelText="İptal"
            >
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    },
  ];

  const malzemeColumns = [
    { title: 'Malzeme', dataIndex: 'malzeme_adi', key: 'malzeme_adi', sorter: (a, b) => a.malzeme_adi.localeCompare(b.malzeme_adi, 'tr') },
    { title: 'Gereken', dataIndex: 'gereken_miktar', render: (val, row) => `${formatSayi(val, 2)} ${row.birim}` },
    { title: 'Eldeki Stok', dataIndex: 'stok_miktari', render: (val, row) => `${formatSayi(val, 2)} ${row.birim}` },
    { title: 'Durum', render: (_, row) => row.yeterli
        ? <Tag color="green">Yeterli</Tag>
        : <Tag icon={<WarningOutlined />} color="red">Eksik: {formatSayi(row.eksik_miktar, 2)} {row.birim}</Tag>
    },
  ];

  const eksikMalzemeler = malzemeIhtiyac.filter(m => !m.yeterli);

  const pdfIndir = (sadeceEksik) => {
    const doc = new jsPDF();
    const tarih = hazirlaForm.getFieldValue('tarih');
    const tarihStr = tarih ? dayjs(tarih).format('DD.MM.YYYY') : dayjs().format('DD.MM.YYYY');
    const baslik = sadeceEksik ? 'Eksik Malzeme Listesi' : 'Malzeme Ihtiyac Listesi';
    const liste = sadeceEksik ? eksikMalzemeler : malzemeIhtiyac;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.text(baslik, 14, 20);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.text(`Uretim Tarihi: ${tarihStr}`, 14, 28);

    const satirlar = liste.map(m => [
      m.malzeme_adi,
      `${formatSayi(m.gereken_miktar, 2)} ${m.birim}`,
      `${formatSayi(m.stok_miktari, 2)} ${m.birim}`,
      m.yeterli ? 'Yeterli' : `Eksik: ${formatSayi(m.eksik_miktar, 2)} ${m.birim}`
    ]);
    autoTable(doc, {
      startY: 34,
      head: [['Malzeme', 'Gereken', 'Eldeki Stok', 'Durum']],
      body: satirlar,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [255, 107, 0] },
    });
    doc.save(`${sadeceEksik ? 'eksik_malzemeler' : 'malzeme_ihtiyac'}_${tarihStr}.pdf`);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Üretimler</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input placeholder="Ara..." prefix={<SearchOutlined />} allowClear onChange={(e) => setAramaMetni(e.target.value)} style={{ width: isMobile ? 110 : 200 }} />
        </Space>
      </div>

      <Card size="small" style={{ marginBottom: 16 }} loading={planLoading}
        title={
          <Space>
            <ScheduleOutlined />
            <span>Günün Planı</span>
            <DatePicker value={planTarih} onChange={(d) => { const t = d || dayjs(); setPlanTarih(t); fetchPlan(t); }} allowClear={false} format="DD.MM.YYYY" />
            <Tag color="orange">{gunAdlari[planTarih.day()]}</Tag>
          </Space>
        }
      >
        {!planMenu && (
          <Alert type="warning" showIcon message="Bu tarih için günün menüsü tanımlı değil. Satış takviminden tanımlayın." />
        )}
        {planMenu && planMusteriler.length === 0 && (
          <Alert type="warning" showIcon message={`${gunAdlari[planTarih.day()]} günü için kişi sayısı olan aktif müşteri bulunamadı.`} />
        )}
        {planMenu && planToplamKisi > 0 && (
          <Row gutter={16} align="top">
            <Col xs={24} md={7}>
              <div style={{ fontSize: 12, color: '#888' }}>Menü</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#FF6B00' }}>
                {planMenu.aciklama || `${planMenu.kalemler.length} kalem`}
              </div>
            </Col>
            <Col xs={8} md={3}>
              <Statistic title="Müşteri" value={planMusteriler.length} />
            </Col>
            <Col xs={8} md={3}>
              <Statistic title="Toplam Kişi" value={planToplamKisi} />
              <div style={{ fontSize: 11, color: '#999' }}>
                Sabit {planSabitToplam} + Sıcak {Number(planEditSicak || 0)}
              </div>
            </Col>
            <Col xs={24} md={11}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Üretilecek (Günün Menüsü)</div>
              <Space wrap size={[4, 6]}>
                {planUretimler.map(p => (
                  <Tag key={p.urun_id} color="blue" style={{ fontSize: 13, padding: '2px 8px', margin: 0 }}>
                    {p.urun_adi} <strong>× {p.toplam}</strong>
                  </Tag>
                ))}
              </Space>
            </Col>
          </Row>
        )}
        {planEkstraGruplari.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #eee' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Sıcak Satış Ekstraları</div>
            {planEkstraGruplari.map((g, i) => (
              <div key={i} style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: '#666', marginRight: 8 }}>{g.baslik}:</span>
                <Space wrap size={[4, 6]}>
                  {g.kalemler.map((k, j) => (
                    <Tag key={j} color="purple" style={{ fontSize: 13, padding: '2px 8px', margin: 0 }}>
                      {k.urun_adi} <strong>× {k.toplam}</strong>
                    </Tag>
                  ))}
                </Space>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Üretim Aşamaları */}
      <Card size="small" style={{ marginBottom: 16 }}
        title={<Space><AppstoreOutlined /><span>Üretim Aşamaları</span></Space>}
      >
        <Row gutter={[12, 12]}>
          {(() => {
            const stageBtnStyle = (done) => ({
              width: '100%',
              height: 'auto',
              minHeight: isMobile ? 64 : 78,
              padding: isMobile ? '8px 10px' : '10px 14px',
              textAlign: 'left',
              whiteSpace: 'normal',
              borderColor: done ? '#52c41a' : undefined,
              background: done ? '#f6ffed' : undefined,
            });
            const renderStage = ({ no, baslik, aciklama, icon, done, onClick, disabled, loading }) => (
              <Button
                size="large"
                onClick={onClick}
                disabled={disabled}
                loading={loading}
                style={stageBtnStyle(done)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: done ? '#52c41a' : '#FF6B00', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, fontWeight: 700,
                  }}>
                    {done ? <CheckOutlined /> : no}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {icon}{baslik}
                      {done && <CheckCircleFilled style={{ color: '#52c41a', marginLeft: 'auto' }} />}
                    </div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{aciklama}</div>
                  </div>
                </div>
              </Button>
            );
            const ts = planTarih.format('YYYY-MM-DD');
            const malzemeKontrolDone = !!malzemeKontrolTamamMap[ts];
            const tarihUretimleri = data.filter(u => (u.tarih ? dayjs(u.tarih).format('YYYY-MM-DD') : null) === ts);
            const uretimTamamDone = tarihUretimleri.length > 0;
            const uretilecekVar = planEditUretilecekler.length > 0;
            return (
              <>
                <Col xs={24} md={8}>
                  {renderStage({
                    no: 1,
                    baslik: 'Üretim Planı',
                    aciklama: 'Sabit müşteri / sıcak satış / ekstra menü düzenle',
                    icon: <ScheduleOutlined />,
                    done: planKayitli,
                    onClick: () => setDetayOpen(true),
                  })}
                </Col>
                <Col xs={24} md={8}>
                  {renderStage({
                    no: 2,
                    baslik: 'Malzeme Kontrol',
                    aciklama: uretilecekVar ? 'Reçete malzemelerini gözden geçir, hazır olanları işaretle' : 'Önce üretim planı oluştur',
                    icon: <CheckOutlined />,
                    done: malzemeKontrolDone,
                    onClick: acMalzemeKontrol,
                    disabled: !uretilecekVar,
                    loading: malzemeKontrolLoading,
                  })}
                </Col>
                <Col xs={24} md={8}>
                  {renderStage({
                    no: 3,
                    baslik: 'Üretim Tamamla',
                    aciklama: uretilecekVar ? 'Üretim kayıtlarını oluştur' : 'Önce üretim planı oluştur',
                    icon: <ExperimentOutlined />,
                    done: uretimTamamDone,
                    onClick: handleHazirlaAc,
                    disabled: !uretilecekVar && !planMenu,
                  })}
                </Col>
              </>
            );
          })()}
        </Row>
      </Card>

      <Table columns={columns} dataSource={filteredData} rowKey="id" loading={loading}
        pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt` }} />

      <Modal
        title="Üretim Hazırla"
        open={hazirlaOpen}
        onCancel={hazirlaKapat}
        width={700}
        footer={[
          <Button key="iptal" onClick={hazirlaKapat}>İptal</Button>,
          <Button key="kaydet" type="primary" onClick={handleHazirlaKaydet}>Kaydet</Button>,
        ]}
      >
        <Form form={hazirlaForm} layout="vertical"
          onValuesChange={(changed) => {
            if (changed.tarih !== undefined) gununMenusunuYukle();
          }}
        >
          <Form.Item name="tarih" label="Üretim Tarihi" rules={[{ required: true, message: 'Tarih seçin' }]} initialValue={dayjs()}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.List name="kalemler" initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item {...restField} name={[name, 'urun_id']} rules={[{ required: true, message: 'Seçin' }]}>
                      <Select
                        options={urunSecenekleri}
                        placeholder="Ürün seçin"
                        style={{ width: 320 }}
                        showSearch
                        filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                      />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'miktar']} rules={[{ required: true, message: 'Miktar' }]}>
                      <InputNumber placeholder="Miktar" min={0.001} step={0.1} style={{ width: 110 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'birim_id']} initialValue={6}>
                      <Select options={birimler} style={{ width: 110 }} />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button type="link" danger onClick={() => remove(name)}>Sil</Button>
                    )}
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Ürün Ekle
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      <Modal
        title={`${planTarih.format('DD.MM.YYYY')} ${gunAdlari[planTarih.day()]} — Üretim Planı`}
        open={detayOpen}
        onCancel={() => setDetayOpen(false)}
        width={720}
        footer={[
          <Button key="iptal" onClick={() => setDetayOpen(false)}>Vazgeç</Button>,
          <Button key="kaydet" type="primary" loading={planSaveLoading} onClick={handlePlanKaydet}>Kaydet</Button>,
        ]}
      >
        <div style={{ marginBottom: 8, fontSize: 12, color: planKayitli ? '#52c41a' : '#999' }}>
          {planKayitli ? '✓ Bu tarih için kayıtlı plan var' : 'Plan henüz kaydedilmedi (haftalık varsayılandan taslak gösteriliyor)'}
        </div>

        {/* Sıcak satış */}
        <Divider orientation="left" style={{ margin: '12px 0 8px' }}>Sıcak Satış</Divider>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <label style={{ fontSize: 12, color: '#666', minWidth: 200 }}>Günün menüsünden (kişi sayısı)</label>
          <InputNumber min={0} value={planEditSicak} onChange={(v) => setPlanEditSicak(v || 0)} style={{ width: 120 }} />
        </div>

        {planEditEkstralar.length > 0 && (
          <Table
            size="small"
            pagination={false}
            dataSource={planEditEkstralar}
            rowKey={(r, i) => `${r.kaynak_tip}-${r.kaynak_id}-${i}`}
            style={{ marginBottom: 8 }}
            columns={[
              {
                title: 'Ekstra Menü/Ürün',
                render: (_, r) => {
                  if (r.kaynak_tip === 'menu') {
                    const m = gununEkstraMenuleri.find(x => x.id === r.kaynak_id);
                    return <span><Tag color="purple">Menü</Tag>{m?.ad || r.ad || '?'}</span>;
                  }
                  const u = urunler.find(x => x.value === r.kaynak_id);
                  return <span><Tag color="cyan">Ürün</Tag>{u?.label || r.ad || '?'}</span>;
                },
              },
              {
                title: 'Adet/Kişi', width: 120, align: 'right',
                render: (_, r, idx) => (
                  <InputNumber
                    min={1}
                    value={r.adet}
                    size="small"
                    style={{ width: 90 }}
                    onChange={(val) => setPlanEditEkstralar(prev => prev.map((p, i) => i === idx ? { ...p, adet: val || 1 } : p))}
                  />
                ),
              },
              {
                title: '', key: 'sil', width: 50, align: 'center',
                render: (_, _r, idx) => (
                  <Button danger size="small" icon={<DeleteOutlined />}
                    onClick={() => setPlanEditEkstralar(prev => prev.filter((_, i) => i !== idx))} />
                ),
              },
            ]}
          />
        )}

        <Space.Compact style={{ width: '100%', marginBottom: 4 }}>
          <Select
            placeholder="Menü veya ürün ekle..."
            value={ekstraEkleSecim}
            onChange={setEkstraEkleSecim}
            showSearch
            optionFilterProp="label"
            style={{ flex: 1 }}
            options={[
              ...(gununEkstraMenuleri.length > 0 ? [{
                label: 'Bu güne tanımlı menüler',
                options: gununEkstraMenuleri.map(m => ({ value: `menu:${m.id}`, label: `[Menü] ${m.ad}` })),
              }] : []),
              {
                label: 'Ürünler',
                options: urunler.map(u => ({ value: `urun:${u.value}`, label: `[Ürün] ${u.label}` })),
              },
            ]}
          />
          <InputNumber min={1} value={ekstraEkleAdet} onChange={(v) => setEkstraEkleAdet(v || 1)} style={{ width: 110 }} placeholder="Adet" />
          <Button icon={<PlusOutlined />} type="primary" onClick={() => {
            if (!ekstraEkleSecim) { message.warning('Menü veya ürün seçin'); return; }
            const [tip, idStr] = ekstraEkleSecim.split(':');
            const id = Number(idStr);
            const adet = Number(ekstraEkleAdet || 1);
            if (planEditEkstralar.some(e => e.kaynak_tip === tip && e.kaynak_id === id)) {
              message.warning('Bu kalem zaten eklenmiş'); return;
            }
            const ad = tip === 'menu'
              ? gununEkstraMenuleri.find(m => m.id === id)?.ad
              : urunler.find(u => u.value === id)?.label;
            setPlanEditEkstralar(prev => [...prev, { kaynak_tip: tip, kaynak_id: id, ad, adet }]);
            setEkstraEkleSecim(null);
            setEkstraEkleAdet(1);
          }}>Ekle</Button>
        </Space.Compact>
        <div style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>
          Menü için adet = kaç kişilik üretileceği. Ürün için adet = kaç tane üretileceği.
        </div>

        {/* Sabit müşteriler — açılır kapanır */}
        <Collapse
          style={{ marginTop: 12, background: '#fafafa' }}
          items={[{
            key: '1',
            label: (
              <Space size={12} wrap>
                <strong>Sabit Müşteriler</strong>
                <Tag color="blue">
                  {planEditMusteriler.filter(m => m.dahil).length} dahil / {planEditMusteriler.length} müşteri
                </Tag>
                <Tag color="orange">{planEditSabitToplam} kişi</Tag>
              </Space>
            ),
            children: planEditMusteriler.length === 0 ? (
              <Alert type="info" message="Bu gün için müşteri yok" />
            ) : (
              <Table
                size="small"
                pagination={false}
                dataSource={planEditMusteriler}
                rowKey="cari_id"
                columns={[
                  {
                    title: 'Dahil', dataIndex: 'dahil', width: 60, align: 'center',
                    render: (v, r) => (
                      <Checkbox
                        checked={!!r.dahil}
                        onChange={(e) => setPlanEditMusteriler(prev => prev.map(p => p.cari_id === r.cari_id ? { ...p, dahil: e.target.checked } : p))}
                      />
                    ),
                  },
                  {
                    title: 'Müşteri', dataIndex: 'ad',
                    sorter: (a, b) => (a.ad || '').localeCompare(b.ad || '', 'tr'),
                    render: (v, r) => <span style={{ color: r.dahil ? undefined : '#bbb' }}>{v}</span>,
                  },
                  {
                    title: 'Kişi', dataIndex: 'kisi', align: 'right', width: 110,
                    render: (v, r) => (
                      <InputNumber
                        min={0}
                        value={r.kisi}
                        size="small"
                        disabled={!r.dahil}
                        style={{ width: 80 }}
                        onChange={(val) => setPlanEditMusteriler(prev => prev.map(p => p.cari_id === r.cari_id ? { ...p, kisi: val || 0 } : p))}
                      />
                    ),
                  },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}><strong>Sabit Toplam (dahil)</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right"><strong>{planEditSabitToplam}</strong></Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            ),
          }]}
        />

        <Divider style={{ margin: '16px 0 8px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px', fontSize: 16 }}>
          <strong>Günün Menüsü Toplam Kişi</strong>
          <strong style={{ color: '#FF6B00', fontSize: 20 }}>{planEditGunMenuKisi}</strong>
        </div>
        <div style={{ fontSize: 11, color: '#999', textAlign: 'right', marginTop: 4 }}>
          Sabit ({planEditSabitToplam}) + Sıcak Satış ({Number(planEditSicak || 0)})
        </div>
      </Modal>

      {/* Malzeme Kontrol Modalı */}
      <Modal
        title="Malzeme Kontrolü"
        open={malzemeKontrolOpen}
        onCancel={() => setMalzemeKontrolOpen(false)}
        width={780}
        footer={[
          <Button key="kapat" onClick={() => setMalzemeKontrolOpen(false)}>Kapat</Button>,
          <Button
            key="ileri"
            type="primary"
            disabled={!malzemeKontrolTumTikli}
            onClick={() => {
              const ts = planTarih.format('YYYY-MM-DD');
              setMalzemeKontrolTamamMap(prev => ({ ...prev, [ts]: true }));
              message.success('Tüm malzemeler hazır. Üretim Tamamla aşamasına geçebilirsiniz.');
              setMalzemeKontrolOpen(false);
            }}
          >
            Üretime Devam {malzemeKontrolTumIds.length > 0 && `(${Object.values(malzemeKontrolTikler).filter(Boolean).length}/${malzemeKontrolTumIds.length})`}
          </Button>,
        ]}
      >
        <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
          Üst tabloda üretilecek ürünler, alt tabloda tüm ürünlerin reçetelerindeki malzemelerin
          toplam ihtiyacı listelenir. Alt taraftan elinizde olan malzemeleri işaretleyin —
          malzemesi tikli ürünler üst tabloda otomatik hazır görünür.
        </div>
        {malzemeKontrolData.length === 0 ? (
          <Alert type="info" message="Üretilecek ürün yok" />
        ) : (
          <>
            <Card size="small" title="Üretilecek Ürünler" style={{ marginBottom: 12 }}>
              <Table
                size="small"
                pagination={false}
                dataSource={malzemeKontrolData}
                rowKey="urun_id"
                onRow={(r) => {
                  const ms = r.malzemeler || [];
                  const hazir = ms.length > 0 && ms.every(m => malzemeKontrolTikler[m.malzeme_id]);
                  return hazir ? { style: { background: '#f6ffed' } } : {};
                }}
                columns={[
                  {
                    title: 'Hazır', key: 'hazir', width: 60, align: 'center',
                    render: (_, r) => {
                      const ms = r.malzemeler || [];
                      if (ms.length === 0) return <span style={{ color: '#bbb' }}>—</span>;
                      const hepsi = ms.every(m => malzemeKontrolTikler[m.malzeme_id]);
                      return hepsi
                        ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
                        : <span style={{ color: '#bbb' }}>○</span>;
                    },
                  },
                  { title: 'Ürün', dataIndex: 'urun_adi' },
                  {
                    title: 'Üretilecek', dataIndex: 'miktar', align: 'right', width: 120,
                    render: (v) => <Tag color="blue">{formatSayi(v, 0)} adet</Tag>,
                  },
                  {
                    title: 'Malzeme', dataIndex: 'malzemeler', align: 'right', width: 110,
                    render: (ms) => {
                      if (!ms || ms.length === 0) return <Tag color="orange">Reçete yok</Tag>;
                      const tikli = ms.filter(m => malzemeKontrolTikler[m.malzeme_id]).length;
                      return <Tag color={tikli === ms.length ? 'green' : 'default'}>{tikli}/{ms.length}</Tag>;
                    },
                  },
                  {
                    title: 'Reçete', key: 'recete', align: 'center', width: 160,
                    render: (_, r) => {
                      const yok = !r.malzemeler || r.malzemeler.length === 0;
                      return (
                        <Button
                          size="small"
                          type={yok ? 'primary' : 'default'}
                          danger={yok}
                          icon={<EditOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setReceteUrun({ id: r.urun_id, urun_adi: r.urun_adi });
                            setReceteOpen(true);
                          }}
                        >
                          {yok ? 'Reçete Ekle' : 'Reçete Güncelle'}
                        </Button>
                      );
                    },
                  },
                ]}
              />
            </Card>

            <Card
              size="small"
              title={
                <Space>
                  <span>Toplam Malzeme İhtiyacı</span>
                  {birlesikMalzemeler.length > 0 && (
                    <Tag color="blue">
                      {birlesikMalzemeler.filter(m => malzemeKontrolTikler[m.malzeme_id]).length}/{birlesikMalzemeler.length}
                    </Tag>
                  )}
                </Space>
              }
            >
              {birlesikMalzemeler.length === 0 ? (
                <Alert type="warning" showIcon message="Hiçbir ürün için reçete tanımlı değil" />
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  dataSource={birlesikMalzemeler}
                  rowKey="malzeme_id"
                  columns={[
                    {
                      title: (
                        <Checkbox
                          checked={birlesikMalzemeler.length > 0 && birlesikMalzemeler.every(m => malzemeKontrolTikler[m.malzeme_id])}
                          indeterminate={
                            birlesikMalzemeler.some(m => malzemeKontrolTikler[m.malzeme_id])
                            && !birlesikMalzemeler.every(m => malzemeKontrolTikler[m.malzeme_id])
                          }
                          onChange={(e) => {
                            const next = { ...malzemeKontrolTikler };
                            birlesikMalzemeler.forEach(m => { next[m.malzeme_id] = e.target.checked; });
                            setMalzemeKontrolTikler(next);
                          }}
                        />
                      ),
                      key: 'tik', width: 60, align: 'center',
                      render: (_, r) => (
                        <Checkbox
                          checked={!!malzemeKontrolTikler[r.malzeme_id]}
                          onChange={(e) => setMalzemeKontrolTikler(p => ({ ...p, [r.malzeme_id]: e.target.checked }))}
                        />
                      ),
                    },
                    { title: 'Malzeme', dataIndex: 'malzeme_adi' },
                    {
                      title: 'Toplam Gerekli', dataIndex: 'toplam_gereken', align: 'right', width: 180,
                      render: (v, r) => `${formatSayi(v, 2)} ${r.birim}`,
                    },
                    {
                      title: 'Kullanan Ürün', dataIndex: 'urun_sayisi', align: 'right', width: 120,
                      render: (v) => <Tag>{v} ürün</Tag>,
                    },
                  ]}
                />
              )}
            </Card>
          </>
        )}
      </Modal>

      <Modal
        title="Üretim Düzenle"
        open={duzenleOpen}
        onCancel={() => { setDuzenleOpen(false); duzenleForm.resetFields(); setDuzenleRecord(null); }}
        onOk={handleDuzenleKaydet}
        okText="Kaydet" cancelText="İptal"
      >
        <Form form={duzenleForm} layout="vertical">
          <Form.Item name="urun_id" label="Ürün" rules={[{ required: true, message: 'Seçin' }]}>
            <Select options={urunSecenekleri} placeholder="Ürün seçin" showSearch
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())} />
          </Form.Item>
          <Form.Item name="miktar" label="Miktar" rules={[{ required: true, message: 'Miktar girin' }]}>
            <InputNumber min={0.001} step={0.1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="birim_id" label="Birim">
            <Select options={birimler} />
          </Form.Item>
          <Form.Item name="tarih" label="Tarih" rules={[{ required: true, message: 'Tarih seçin' }]} initialValue={dayjs()}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
        {duzenleRecord?.durum === 'onaylandi' && (
          <Alert type="info" showIcon message="Onaylanmış üretim düzenlendiğinde stok hareketleri yeniden hesaplanacaktır." />
        )}
      </Modal>

      <UrunDetayModal
        open={urunDetayOpen}
        urun={urunDetayKayit}
        onClose={() => { setUrunDetayOpen(false); setUrunDetayKayit(null); }}
      />

      <ReceteModal
        open={receteOpen}
        urun={receteUrun}
        onClose={() => { setReceteOpen(false); setReceteUrun(null); }}
        onSaved={() => { refreshMalzemeKontrol(); fetchUrunler(); }}
      />
    </div>
  );
}

export default Uretimler;
